//= require personalised-config.js
//= require personalised-utils.js
//= require personalised-validation.js
// CUSTOM: personalised-bundle-utils.js

(function() {
  window.personalised = window.personalised || {};
  
  // Utility functions for bundle operations
  const bundleUtils = {
    buildPayload: function(bundleProductId, bundleVariantId) {
      // Define a valid default payload structure
      const defaultPayload = {
        externalProductId: bundleProductId || null,
        externalVariantId: bundleVariantId || null,
        selections: []
      };
      
      // Validate inputs and state availability
      if (!bundleProductId || !bundleVariantId) {
        console.warn('Bundle buildPayload: Missing product or variant ID');
        return defaultPayload;
      }
      
      // Validate bundle state is loaded and has items
      if (!window.personalised.validation.validateBundleLoaded()) {
        console.warn('Bundle buildPayload: Bundle not loaded or empty');
        return defaultPayload;
      }
      
      // Get items once to ensure consistency
      const bundleItems = window.personalised.bundle.getItems();
      
      // Proceed with normal behavior when all validations pass
      return {
        externalProductId: bundleProductId,
        externalVariantId: bundleVariantId,
        selections: bundleItems.map(item => ({
          collectionId: item.collectionId || null,
          externalProductId: item.productId || null,
          externalVariantId: item.variantId || null,
          quantity: (item.quantity || 1) * window.personalised.bundle.getFrequencyMultiplier(),
          sellingPlan: item.sellingPlanId || null
        }))
      };
    },    
    
    getBundlePayload: function () {
      try {
        const bundleProductId = window.personalised.config && window.personalised.config.bundleProductId;
        const bundleVariantId = window.personalised.config && window.personalised.config.bundleVariantId;
        
        // Directly call the buildPayload function
        return this.buildPayload(bundleProductId, bundleVariantId);
      } catch (error) {
        console.error('Error building bundle payload:', error);
        // Return a safe minimal payload to prevent breaking downstream code
        return { selections: [] };
      }
    },
      // Private implementation shared by both refresh methods    
    _executeCartRefresh: async function(message = null, useDebounced = false) {
      window.personalised?.debug?.time('cartSync:executeCartRefresh');
      window.personalised?.debug?.trace('cartSync:executeCartRefreshStart', { useDebounced: useDebounced });
      try {
        
        if (window.personalised?.config?.debugMode) {
          // PR-4.3: window.personalised._loadedFromRecharge is never set; real flag is in metadata.
          console.debug('[_executeCartRefresh] Cart sync check state:', {
            loadedFromRecharge: !!window.personalised.storageManager?.getMetadata('loadedFromRecharge'),
            initializationInProgress: !!window.personalised?._initializationInProgress, // known dead
            currentMode: window.personalised.utils.getMode(),
            editMode: window.personalised?.config?.modes?.EDIT || 'edit',
            isEditMode: window.personalised.utils.getMode() === (window.personalised?.config?.modes?.EDIT || 'edit')
          });
        }
        
        // Cart sync safeguard: prevent rebuild if bundle came from Recharge.
        // PR-4.3: was checking window.personalised._loadedFromRecharge (never set — dead code).
        // Now checks the actual metadata flag set by bundle-state.initializeEditMode().
        if (window.personalised.storageManager?.getMetadata('loadedFromRecharge')) {
          window.personalised?.debug?.trace('cartSync:guardSkipped', { reason: 'loadedFromRecharge' });
          window.personalised?.debug?.timeEnd('cartSync:executeCartRefresh');
          console.info('Skipping cart sync: bundle loaded from Recharge');
          return [];
        }
        
        // Cart sync safeguard: prevent rebuild if initialization is in progress.
        // PR-4.3: window.personalised._initializationInProgress is never set (real lock is
        // IIFE-private in bundle-state.js). Edit-mode guard below covers the init scenario.
        if (window.personalised?._initializationInProgress) {
          window.personalised?.debug?.trace('cartSync:guardSkipped', { reason: 'initializationInProgress' });
          window.personalised?.debug?.timeEnd('cartSync:executeCartRefresh');
          console.info('Skipping cart sync: initialization in progress');
          return [];
        }

        // Skip cart sync entirely in edit mode
        if (window.personalised.utils.getMode() === window.personalised.config.modes.EDIT) {
          window.personalised?.debug?.trace('cartSync:guardSkipped', { reason: 'editMode' });
          window.personalised?.debug?.timeEnd('cartSync:executeCartRefresh');
          console.info('Skipping cart sync: edit mode active');
          return [];
        }
        
        // Validate that bundle state is loaded
        if (!window.personalised.validation.validateBundleLoaded()) {
          window.personalised?.debug?.trace('cartSync:guardSkipped', { reason: 'bundleNotLoaded' });
          window.personalised?.debug?.timeEnd('cartSync:executeCartRefresh');
          console.error('Bundle state not loaded');
          if (window.personalised.eventBus) {
            window.personalised.eventBus.emit('syncFailed', { 
              error: new Error('Bundle state not loaded'),
              message: 'Unable to update cart: bundle state not available'
            });
          }
          return [];
        }

        // PR-2.2: Skip sync if state is identical to last successfully synced state.
        // The module-level _lastSyncedStateHash resets on every page load, so the first
        // sync of each session always runs (covers direct navigation / stale cart).
        const syncedHash = (window.personalised.cartSync &&
          typeof window.personalised.cartSync._computeStateHash === 'function')
          ? window.personalised.cartSync._computeStateHash()
          : null;
        if (syncedHash !== null && syncedHash === window.personalised.cartSync._lastSyncedStateHash) {
          window.personalised?.debug?.trace('cartSync:skipped:stateUnchanged', { hash: syncedHash });
          window.personalised?.debug?.timeEnd('cartSync:executeCartRefresh');
          return [];
        }

        // PR-2.4: Timestamp-based skip guard — cheaper than _isCartFreshForCurrentState (zero API calls).
        // Skip defensive sync entirely if a sync completed recently AND bundle has not been modified since.
        // Applies to the normal library→review flow where cart is already guaranteed fresh.
        // Fails open: any missing/invalid value falls through to the cart-freshness check below.
        if (window.personalised.cartSync &&
            window.personalised.cartSync._lastSyncedStateHash === null) {
          const lastSyncTs = (window.personalised.storageManager &&
            typeof window.personalised.storageManager.getLastSyncTimestamp === 'function')
            ? window.personalised.storageManager.getLastSyncTimestamp()
            : null;
          const bundleModifiedSinceSync = (window.personalised.storageManager &&
            typeof window.personalised.storageManager.isBundleModified === 'function')
            ? window.personalised.storageManager.isBundleModified()
            : true; // fail open if storageManager unavailable
          if (lastSyncTs !== null && !bundleModifiedSinceSync) {
            const ageMs = Date.now() - lastSyncTs;
            const threshold = (window.personalised.config.timing &&
              window.personalised.config.timing.syncRecencyThresholdMs) || 60000;
            if (ageMs < threshold) {
              window.personalised?.debug?.trace('cartSync:skipped:recentSyncTimestamp', { ageMs: ageMs, threshold: threshold });
              window.personalised?.debug?.timeEnd('cartSync:executeCartRefresh');
              return [];
            }
          }
        }

        // PR-2.2: Fresh-session cart-staleness guard (for review-page defensive sync).
        // When no in-memory sync proof exists (_lastSyncedStateHash === null), the hash
        // check above cannot suppress the sync. Do a lightweight GET /cart.js to check
        // whether the cart already reflects the current bundle state before running the
        // full 4-5 API call rebuild. If the cart is already correct: skip, warm the hash.
        // This is the mechanism that prevents the review page from blindly rebuilding on
        // every page reload / fresh direct navigation when the cart is already correct.
        if (window.personalised.cartSync &&
            window.personalised.cartSync._lastSyncedStateHash === null &&
            typeof window.personalised.cartSync._isCartFreshForCurrentState === 'function') {
          try {
            const cartFresh = await window.personalised.cartSync._isCartFreshForCurrentState();
            if (cartFresh) {
              window.personalised?.debug?.trace('cartSync:skipped:reviewPageCartFresh', { hash: syncedHash });
              // Warm in-memory hash so subsequent same-state user interactions are also suppressed.
              if (syncedHash !== null) {
                window.personalised.cartSync._lastSyncedStateHash = syncedHash;
                window.personalised?.debug?.trace('cartSync:hashStoredFromCartCheck', { hash: syncedHash });
              }
              window.personalised?.debug?.timeEnd('cartSync:executeCartRefresh');
              return [];
            }
          } catch (cartCheckErr) {
            // Fail open: any error in the cart check means we proceed with the sync.
            window.personalised?.debug?.trace('cartSync:cartFreshCheckError', { message: cartCheckErr && cartCheckErr.message });
          }
        }
        
        // Check if there are items in the bundle
        if (!window.personalised.validation.bundleHasItems()) {
          window.personalised?.debug?.trace('cartSync:bundleEmpty', {});
          // Handle empty state - just remove all bundle items from cart
          if (window.personalised.cartSync) {
            try {
              const emptyBundleResult = await window.personalised.cartSync.rebuildInCart([]);
              // PR-2.2: Store hash on success only — do NOT store on failure or the
              // hash guard will suppress the next sync attempt against a stale cart.
              if (emptyBundleResult !== false && syncedHash !== null) {
                window.personalised.cartSync._lastSyncedStateHash = syncedHash;
              }
              // PR-2.4: Write timestamp + clear modified flag only on actual success.
              if (emptyBundleResult !== false) {
                window.personalised.storageManager?.setLastSyncTimestamp();
                window.personalised.storageManager?.clearModifiedFlag();
                window.personalised?.debug?.trace('cartSync:syncTimestampStored', { path: 'emptyBundle-immediate' });
              }
            } catch (syncError) {
              console.error('Error syncing empty state to cart:', syncError);
              if (window.personalised.eventBus) {
                window.personalised.eventBus.emit('syncFailed', { 
                  error: syncError,
                  message: 'Failed to clear cart items'
                });
              }
            }
          }
          window.personalised?.debug?.timeEnd('cartSync:executeCartRefresh');
          return [];
        }
          // Get bundle payload with error handling already built in
        window.personalised?.debug?.time('cartSync:buildPayload');
        const payload = window.personalised.utils.getBundlePayload();
        window.personalised?.debug?.timeEnd('cartSync:buildPayload');
        
        // Use the BUNDLE_PARENT_PROPERTY_KEY from config
        const bundleParentKey = window.personalised.config && 
          window.personalised.config.bundle?.BUNDLE_PARENT_PROPERTY_KEY || 'shopifyProductHandle';
          // Get dynamic bundle items with error handling
        let lineItems = [];
        
        try {
          // Wait for the SDK to be initialized
          window.personalised?.debug?.time('cartSync:waitForRechargeSDK');
          await window.personalised.utils.waitForRechargeSDK();
          window.personalised?.debug?.timeEnd('cartSync:waitForRechargeSDK');

          // Ensure the method is available before calling
          if (!window.recharge?.bundle?.getDynamicBundleItems) {
            throw new Error('Recharge bundle SDK method not available');
          }
          
          window.personalised?.debug?.time('cartSync:getDynamicBundleItems');
          lineItems = await window.recharge.bundle.getDynamicBundleItems(payload, bundleParentKey);
          window.personalised?.debug?.timeEnd('cartSync:getDynamicBundleItems');
          window.personalised?.debug?.trace('cartSync:lineItemsResolved', { count: Array.isArray(lineItems) ? lineItems.length : 0 });
          
          // Validate returned lineItems
          if (!Array.isArray(lineItems)) {
            throw new Error('Invalid line items response from Recharge SDK');
          }
        } catch (rechargeError) {
          window.personalised?.debug?.trace('cartSync:rechargeSDKError', { message: rechargeError && rechargeError.message });
          window.personalised?.debug?.timeEnd('cartSync:executeCartRefresh');
          console.error('Error with Recharge SDK:', rechargeError);
          
          // Optional: emit syncFailed to gracefully inform the UI or calling logic
          if (window.personalised.eventBus) {
            window.personalised.eventBus.emit('syncFailed', { 
              error: rechargeError,
              message: 'Failed to prepare your bundle items'
            });
          }
          
          // Fallback: Return empty result to avoid runtime failure
          return [];
        }
        
        // Immediate cart sync (used by refreshCartFromState only after PR-2.1;
        // debounced path now goes through cartSync.scheduleSync directly).
        let rebuildResult;
        try {
          if (!window.personalised.cartSync) {
            throw new Error('Cart sync module not found');
          }
          if (typeof window.personalised.cartSync.rebuildInCart === 'function') {
            rebuildResult = await window.personalised.cartSync.rebuildInCart(lineItems);
          } else {
            throw new Error('Cart sync rebuildInCart method not available');
          }
        } catch (syncError) {
          window.personalised?.debug?.trace('cartSync:syncError', { message: syncError && syncError.message });
          window.personalised?.debug?.timeEnd('cartSync:executeCartRefresh');
          console.error('Error syncing bundle to cart:', syncError);
          if (window.personalised.eventBus) {
            window.personalised.eventBus.emit('syncFailed', { 
              error: syncError,
              message: 'Failed to update your cart'
            });
          }
          return lineItems; // Still return lineItems for use by caller
        }

        // PR-2.2: Store hash on success only — do NOT store on failure or the
        // hash guard will suppress the next sync attempt against a stale cart.
        if (rebuildResult !== false && syncedHash !== null && window.personalised.cartSync) {
          window.personalised.cartSync._lastSyncedStateHash = syncedHash;
          window.personalised?.debug?.trace('cartSync:hashStored', { hash: syncedHash });
        }
        // PR-2.4: Write timestamp + clear modified flag only on actual success.
        if (rebuildResult !== false) {
          window.personalised.storageManager?.setLastSyncTimestamp();
          window.personalised.storageManager?.clearModifiedFlag();
          window.personalised?.debug?.trace('cartSync:syncTimestampStored', { path: 'fullSync-immediate' });
        }
        window.personalised?.debug?.trace('cartSync:executeCartRefreshComplete', { lineItemCount: lineItems.length, useDebounced: useDebounced });
        window.personalised?.debug?.timeEnd('cartSync:executeCartRefresh');
        return lineItems;
      } catch (error) {
        // Catch any other unexpected errors
        window.personalised?.debug?.trace('cartSync:executeCartRefreshError', { message: error && error.message });
        window.personalised?.debug?.timeEnd('cartSync:executeCartRefresh');
        console.error(`Unexpected error in cart sync (${useDebounced ? 'debounced' : 'immediate'}):`, error);
        if (window.personalised.eventBus) {
          window.personalised.eventBus.emit('syncFailed', { 
            error: error,
            message: 'An unexpected error occurred'
          });
        }
        return [];
      }
    },    
    
    // Public methods that leverage the shared implementation
    refreshCartFromState: async function(message = null) {
      // Use shared implementation with immediate sync
      return this._executeCartRefresh(message, false);
    },
      // Debounced version to replace direct calls in event handlers.
    // PR-2.1: delegates immediately to cartSync.scheduleSync which owns the outer
    // debounce timer, syncPending flag, and at-most-one-pending coalescing state.
    // State (getDynamicBundleItems) is read lazily at debounce-fire time, not here.
    debouncedRefreshCartFromState: function(message = null) {
      window.personalised?.debug?.trace('cartSync:debouncedRefreshReceived', {});
      if (window.personalised.cartSync && typeof window.personalised.cartSync.scheduleSync === 'function') {
        window.personalised.cartSync.scheduleSync(message);
      }
    }
  };
  
  // Price calculator
  const priceCalculator = {
    // Calculate total price of all items in bundle
    calculateTotalPrice: function(items, variantLookup) {
      let totalCents = 0;
      if (items && variantLookup) {
        items.forEach(item => {
          const lookup = variantLookup[String(item.variantId)];
          if (lookup && lookup.variant && typeof lookup.variant.price_cents === 'number') {
            totalCents += lookup.variant.price_cents;
          }
        });
      }
      return totalCents;
    },  
    
    // Apply frequency multiplier to price
    applyFrequencyMultiplier: function(totalCents, frequency) {
      // Get the multiplier - use the standardized function from config
      const packCount = window.personalised.config.getFrequencyMultiplier(frequency);
      return totalCents * packCount;
    },
    
    // Apply discount based on frequency
    applyFrequencyDiscount: function(totalCentsWithMultiplier, frequency) {
      const freq = parseInt(frequency, 10) || 30;
      let discountedTotal = totalCentsWithMultiplier;
      
      // Get discount values from config
      const discounts = window.personalised.config.subscription.discounts;
      
      // Apply discount for 60 or 90 day frequency
      if (freq === 60) {
        discountedTotal = Math.round(totalCentsWithMultiplier * (1 - discounts[60])); // Using config discount value
      } else if (freq === 90) {
        discountedTotal = Math.round(totalCentsWithMultiplier * (1 - discounts[90])); // Using config discount value
      }
      
      return discountedTotal;
    },

    // Calculate per day price
    calculatePerDayPrice: function(totalCentsDiscounted, frequency) {
      const freq = parseInt(frequency, 10) || 30;
      let perDay = 0;
      
      if (totalCentsDiscounted > 0 && freq > 0) {
        perDay = (totalCentsDiscounted / 100) / freq;
      }
      
      return perDay;
    },

    // Format price to currency string
    formatPrice: function(cents, includeCurrency = true) {
      const amount = cents > 0 ? (cents / 100).toLocaleString(undefined, { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      }) : '0.00';
      
      return includeCurrency ? `$${amount}` : amount;
    },
    
    // Calculate the before discount price (original price)
    calculateBeforeDiscountPrice: function(totalCentsWithMultiplier, frequency) {
      const freq = parseInt(frequency, 10) || 30;
      
      // For 30-day frequency, return the same price (no discount)
      if (freq === 30) {
        return totalCentsWithMultiplier;
      }
      
      // For 60-day and 90-day, we need to return the non-discounted price
      // which is already correctly calculated as totalCentsWithMultiplier
      return totalCentsWithMultiplier;
    },  
    
    // Get pack count based on frequency - this is just an alias for getFrequencyMultiplier
    getPackCount: function(frequency) {
      return window.personalised.config.getFrequencyMultiplier(frequency);
    }
  };

  // Assign to single namespace
  window.personalised.utils = Object.assign(window.personalised.utils || {}, bundleUtils);
  window.personalised.priceCalculator = priceCalculator;
})();

// NOTE: All event handling for [data-add-to-pack] is now delegated in personalised-bundle-manager.js
// Use data-* attributes for any future selectors or event logic
